import { useEffect, useState } from "react"
import type { Dispatch, SetStateAction } from "react"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { ChatSessionModel } from "@/api/models"
import type { ChatService } from "@/services/chat-service"

import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"
import { queryKeys } from "@/lib/query-keys"

import { useChatSessionSyncPublisher } from "./use-chat-session-cross-tab-sync"

type RenameChatSessionPayload = {
  sessionId: string
  title: string
}

type UseRenameChatSessionArgs = {
  sessionType: string
  chatService: ChatService
  setSelectedSessions: Dispatch<SetStateAction<ChatSessionModel[]>>
  setCurrentDropdownChatSession: Dispatch<SetStateAction<ChatSessionModel | null>>
  selectMode: boolean
}

function renameChatSession({ sessionId, title }: RenameChatSessionPayload) {
  return api.put("/update-session", { session_id: sessionId, title })
}

export function useRenameChatSession({
  sessionType,
  chatService,
  setSelectedSessions,
  setCurrentDropdownChatSession,
  selectMode,
}: UseRenameChatSessionArgs) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [renamingSessionId, setRenamingSessionId] = useState<string | null>(null)
  // Cross-window sync (issue #2548). Scoped to the same unscoped
  // `sessionHistory(sessionType)` key this mutation itself invalidates.
  const publishSessionChange = useChatSessionSyncPublisher({ sessionType })

  const renameChatSessionMutation = useMutation({
    mutationKey: queryKeys.renameChatSession,
    mutationFn: renameChatSession,
    onMutate: async (variables) => {
      const { sessionId, title } = variables

      // Cancel outgoing queries
      await queryClient.cancelQueries({
        queryKey: queryKeys.sessionHistory(sessionType),
      })

      // Snapshot previous sessions
      const previousSessions = queryClient.getQueryData<ChatSessionModel[]>(
        queryKeys.sessionHistory(sessionType)
      )

      // Optimistically update the cache (clear pending suggestion and mark user-set)
      const optimisticSession = (s: ChatSessionModel) =>
        s && s.id === sessionId
          ? { ...s, title, pending_title_suggestion: undefined, user_set_title: true }
          : s

      if (previousSessions) {
        queryClient.setQueryData<ChatSessionModel[]>(
          queryKeys.sessionHistory(sessionType),
          previousSessions.map(optimisticSession)
        )
      }

      // Also update local state optimistically
      setSelectedSessions((prev) => prev.map(optimisticSession))
      setCurrentDropdownChatSession((prev) =>
        prev && prev.id === sessionId
          ? { ...prev, title, pending_title_suggestion: undefined, user_set_title: true }
          : prev
      )

      // Update ChatService optimistically
      chatService.updateSessionTitle(sessionId, title)

      return { previousSessions }
    },
    onSuccess: async () => {
      setRenamingSessionId(null)

      // Invalidate to get fresh data from server
      await queryClient.invalidateQueries({
        queryKey: queryKeys.sessionHistory(sessionType),
      })

      // …and tell this browser's other windows to do the same. Published
      // only on SUCCESS: a rename that the server rejected is rolled back
      // here, and a hint would send the other windows to refetch a list that
      // never changed.
      publishSessionChange("renamed")

      toast.success(t("chatsession.rename_success", { defaultValue: "Session renamed" }))
    },
    onError: (error, variables, context) => {
      const { sessionId } = variables

      // Rollback optimistic update
      if (context?.previousSessions) {
        queryClient.setQueryData<ChatSessionModel[]>(
          queryKeys.sessionHistory(sessionType),
          context.previousSessions
        )

        // Also rollback local state
        const previousSession = context.previousSessions.find((s) => s && s.id === sessionId)
        if (previousSession) {
          setSelectedSessions((prev) =>
            prev.map((session) => (session && session.id === sessionId ? previousSession : session))
          )
          setCurrentDropdownChatSession((prev) =>
            prev && prev.id === sessionId ? previousSession : prev
          )
          chatService.updateSessionTitle(sessionId, previousSession.title || "")
        }
      }

      logger.error("Failed to rename chat session", error as Error)
      toast.error(t("chatsession.rename_error", { defaultValue: "Unable to rename session" }))
    },
  })

  const startRenamingSession = (session: ChatSessionModel) => {
    if (!session?.id) {
      return
    }

    setRenamingSessionId(session.id)
  }

  const cancelRenamingSession = () => {
    setRenamingSessionId(null)
  }

  const handleRenameSessionTitle = async (session: ChatSessionModel, proposedTitle: string) => {
    if (!session?.id) {
      return
    }

    const trimmedTitle = proposedTitle.trim()

    if (!trimmedTitle) {
      toast.error(t("chatsession.rename_required", { defaultValue: "Please enter a title" }))
      return
    }

    if (trimmedTitle.length > 100) {
      toast.error(
        t("chatsession.rename_too_long", { defaultValue: "Title must be less than 100 characters" })
      )
      return
    }

    if (trimmedTitle === (session.title ?? "").trim()) {
      setRenamingSessionId(null)
      return
    }

    try {
      await renameChatSessionMutation.mutateAsync({
        sessionId: session.id,
        title: trimmedTitle,
      })
    } catch (error) {
      logger.debug("Rename chat session mutation rejected", error as Error)
    }
  }

  useEffect(() => {
    if (selectMode && renamingSessionId) {
      setRenamingSessionId(null)
    }
  }, [selectMode, renamingSessionId])

  return {
    renamingSessionId,
    startRenamingSession,
    cancelRenamingSession,
    handleRenameSessionTitle,
    renameChatSessionMutation,
  }
}
